import React, { useMemo } from "react";
import AuthErrorBoundary from './components/Auth/AuthErrorBoundary';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
} from "react-router-dom";
import { ThemeProvider, CssBaseline } from "@mui/material";
import { QueryClient, QueryClientProvider } from "react-query";
import { ReactQueryDevtools } from "react-query/devtools";

// Theme and styles
import { createAppTheme, createAdminTheme } from "./theme";
import { useThemeStore } from "./stores/themeStore";

// Layouts
import MainLayout from "./layouts/MainLayout";
import AdminLayout from "./layouts/AdminLayout";

// Pages
import HomePage from "./pages/HomePage";
import OutfitDetailPage from "./pages/OutfitDetailPage";
import UploadPage from "./pages/UploadPage";
import ProfilePage from "./pages/ProfilePage";
import SearchPage from "./pages/SearchPage";
import DiscoverPage from "./pages/DiscoverPage";
import FeedPage from "./pages/FeedPage";
import LoginPage from "./pages/LoginPage";
import NotFoundPage from "./pages/NotFoundPage";
import PrivacySettingsPage from "./pages/PrivacySettingsPage";
import SettingsPage from "./pages/SettingsPage";
import LiveStreamPage from "./pages/LiveStreamPage";
import BroadcastPage from "./pages/BroadcastPage";
import BulkUploadPage from "./pages/admin/BulkUploadPage";
import WatchStreamPage from "./pages/WatchStreamPage";
import NotificationsPage from "./pages/NotificationsPage";
import BlogListPage from "./pages/BlogListPage";
import BlogArticlePage from "./pages/BlogArticlePage";
import ArticleEditorPage from "./pages/ArticleEditorPage";
import AboutPage from "./pages/AboutPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsOfServicePage from "./pages/TermsOfServicePage";
import ContactPage from "./pages/ContactPage";
import FAQPage from "./pages/FAQPage";
import CategoryLandingPage from "./pages/CategoryLandingPage";
import ModerationDashboard from "./pages/admin/ModerationDashboard";

// Auth
import { AuthProvider } from "./contexts/AuthContext";
import ProtectedRoute from "./components/Auth/ProtectedRoute";
import activityTracker from './services/ActivityTracker';

// Notifications
import { NotificationProvider } from "./contexts/NotificationContext";

// Privacy
import { PrivacyProvider } from "./contexts/PrivacyContext";

// Error Handling
import ErrorBoundary from "./components/ErrorHandling/ErrorBoundary";
import { ToastProvider } from "./contexts/ToastContext";

// PWA Components
import OfflineIndicator from "./components/PWA/OfflineIndicator";

// SEO
import { HelmetProvider } from "react-helmet-async";


// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Memoized admin theme — created once since it never changes
const adminTheme = createAdminTheme();

/**
 * Layout route wrapper that applies the dynamic user-selected theme.
 * Reads the active palette from the Zustand theme store and creates
 * the corresponding MUI theme. All main app routes render inside this.
 */
const DynamicThemeLayout: React.FC = () => {
  const palette = useThemeStore((state) => state.palette);
  const appTheme = useMemo(() => createAppTheme(palette), [palette]);

  return (
    <ThemeProvider theme={appTheme}>
      <CssBaseline />
      <OfflineIndicator />
      <Outlet />
    </ThemeProvider>
  );
};

/**
 * Layout route wrapper that applies the fixed admin theme.
 * Does NOT read from useThemeStore — admin pages are always
 * rendered with the same theme regardless of user preference.
 * Admin routes will be added in task 2.3 with AdminLayout.
 */
const AdminThemeLayout: React.FC = () => {
  return (
    <ThemeProvider theme={adminTheme}>
      <CssBaseline />
      <Outlet />
    </ThemeProvider>
  );
};

const App: React.FC = () => {
  // Initialize activity tracker on mount
  React.useEffect(() => {
    activityTracker.init();
    return () => activityTracker.destroy();
  }, []);

  return (
    <ErrorBoundary>
      <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <Router>
          <ToastProvider>
            <PrivacyProvider>
              <AuthErrorBoundary>
                <AuthProvider>
                  <NotificationProvider>
                  <Routes>
                    {/* ── Admin routes: fixed admin theme (isolated from user theme) ── */}
                    <Route element={<AdminThemeLayout />}>
                      <Route path="/admin" element={<AdminLayout><Outlet /></AdminLayout>}>
                        {/* Admin sub-routes go here as they are formalized */}
                        <Route path="moderation" element={<ModerationDashboard />} />
                      </Route>
                    </Route>

                    {/* ── Main app routes: dynamic theme from themeStore ── */}
                    <Route element={<DynamicThemeLayout />}>
                      {/* Public routes */}
                      <Route path="/login" element={<LoginPage />} />

                      {/* Routes with main layout */}
                      <Route path="/" element={<MainLayout />}>
                        <Route index element={<HomePage />} />
                        <Route path="search" element={<SearchPage />} />
                        <Route path="discover" element={<DiscoverPage />} />
                        <Route path="feed" element={<FeedPage />} />
                        <Route path="outfit/:id" element={<OutfitDetailPage />} />
                        <Route path="live" element={<LiveStreamPage />} />
                        <Route
                          path="live/watch/:streamId"
                          element={
                            <ProtectedRoute>
                              <WatchStreamPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="live/broadcast"
                          element={
                            <ProtectedRoute>
                              <BroadcastPage />
                            </ProtectedRoute>
                          }
                        />

                        {/* Blog routes */}
                        <Route path="blog" element={<BlogListPage />} />
                        <Route path="blog/:slug" element={<BlogArticlePage />} />
                        <Route
                          path="blog/new"
                          element={
                            <ProtectedRoute>
                              <ArticleEditorPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="blog/edit/:articleId"
                          element={
                            <ProtectedRoute>
                              <ArticleEditorPage />
                            </ProtectedRoute>
                          }
                        />

                        {/* Policy & trust pages */}
                        <Route path="about" element={<AboutPage />} />
                        <Route path="privacy-policy" element={<PrivacyPolicyPage />} />
                        <Route path="terms-of-service" element={<TermsOfServicePage />} />
                        <Route path="contact" element={<ContactPage />} />
                        <Route path="faq" element={<FAQPage />} />

                        {/* Category landing pages */}
                        <Route path="styles/:slug" element={<CategoryLandingPage />} />
                        <Route path="occasions/:slug" element={<CategoryLandingPage />} />

                        {/* Protected routes */}
                        <Route
                          path="upload"
                          element={
                            <ProtectedRoute>
                              <UploadPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="profile"
                          element={
                            <ProtectedRoute>
                              <ProfilePage />
                            </ProtectedRoute>
                          }
                        />
                        <Route path="profile/:userId" element={<ProfilePage />} />
                        <Route path="privacy-settings" element={<PrivacySettingsPage />} />
                        <Route path="settings" element={<SettingsPage />} />
                        <Route path="notifications" element={<NotificationsPage />} />
                        <Route
                          path="admin/bulk-upload"
                          element={
                            <ProtectedRoute>
                              <BulkUploadPage />
                            </ProtectedRoute>
                          }
                        />
                      </Route>

                      {/* 404 route */}
                      <Route path="404" element={<NotFoundPage />} />
                      <Route path="*" element={<Navigate to="/404" replace />} />
                    </Route>
                  </Routes>
                  </NotificationProvider>
                </AuthProvider>
              </AuthErrorBoundary>
            </PrivacyProvider>
          </ToastProvider>
        </Router>
        {process.env.NODE_ENV === "development" && <ReactQueryDevtools />}
      </QueryClientProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
};

export default App;
